import { supabase } from './supabase';
import { logger } from './logger';
import { POWERJOBS_USER_ID } from './supportService';

// ─── Types fiche 360 candidat ────────────────────────────────────────

export interface CandidateExperience360 {
  id: string;
  companyName: string;
  city: string;
  sectorName: string;
  positionName: string;
  yearsExperience: string;
  workRhythm: string | null;
}

export interface CandidateVideo360 {
  id: string;
  thumbnailUrl: string | null;
  status: string;
  analysisVerdict: string | null;
  mediaType: string | null;
  createdAt: string;
}

export interface CandidateApplication360 {
  id: string;
  status: string;
  appliedAt: string;
  positionName: string;
  establishmentName: string;
  city: string;
  offerId: string;
}

export interface AccountChange360 {
  id: string;
  occurredAt: string;
  tableName: string;
  rowId: string | null;
  op: string;
  source: string;
  origin: string | null;
  actorId: string | null;
  changedFields: string[];
  clearedFields: string[];
  blockedClears: Record<string, { from?: unknown; payload?: unknown }>;
  changes: Record<string, any>;
  hasWipe: boolean;
}

export interface Candidate360 {
  profileId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  department: string;
  photoUrl: string | null;
  tagline: string;
  status: string;
  seedTag: string | null;
  registeredAt: string;
  lastLoginAt: string | null;
  profileComplete: boolean;
  identityVerified: boolean;
  hasDrivingLicense: boolean;
  mobilityMinutes: number | null;
  desiredWorkRhythm: string | null;
  desiredContractTypes: string[] | null;
  desiredSalaryMin: number | null;
  desiredSalaryMax: number | null;
  availabilityType: string | null;
  availabilityDate: string | null;
  languages: { language: string; level: number }[];
  experiences: CandidateExperience360[];
  videos: CandidateVideo360[];
  applications: CandidateApplication360[];
  changeLog: AccountChange360[];
  stats: {
    profileViewsReceived: number;
    favoritesMade: number;
    swipesMade: number;
  };
}

// ─── Types fiche 360 recruteur ───────────────────────────────────────

export interface EmployerEstablishment360 {
  id: string;
  name: string;
  zipcode: string;
  city: string;
  locationText: string | null;
  logoUrl: string | null;
  photos: string[];
  createdAt: string;
  updatedAt: string | null;
}

export interface EmployerOffer360 {
  id: string;
  sectorCode: string;
  positionCode: string;
  positionName: string;
  establishmentName: string;
  establishmentAddress: string;
  establishmentZipcode: string;
  city: string;
  yearsExperience: string | null;
  contractType: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  workDuration: string | null;
  workRhythm: string[];
  salaryMin: number | null;
  salaryMax: number | null;
  salaryType: string | null;
  requiredDrivingLicense: boolean;
  requiredDiplomas: string[];
  advantages: string[];
  otherInfo: string;
  requiredSkills: string[];
  requiredLanguages: { language: string; level: number }[];
  status: string;
  applicationsCount: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  seedTag: string | null;
  revision: number | null;
}

export interface EmployerVideo360 {
  id: string;
  thumbnailUrl: string | null;
  videoUrl: string | null;
  status: string;
  title: string | null;
  videoType: string | null;
  mediaType: string | null;
  durationSeconds: number | null;
  textOverlay: string | null;
  tags: string[];
  analysisStatus: string | null;
  analysisVerdict: string | null;
  offerId: string | null;
  muxPlaybackId: string | null;
  createdAt: string;
}

export interface EmployerSubscription360 {
  id: string;
  plan: string;
  status: string;
  paymentMethod: string | null;
  amountHt: number | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
}

export interface EmployerInvoice360 {
  id: string;
  stripeInvoiceId: string | null;
  amount: number | null;
  status: string;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  pdfUrl: string | null;
  createdAt: string;
}

export interface EmployerApplication360 {
  id: string;
  status: string;
  appliedAt: string;
  viewedAt: string | null;
  selectedAt: string | null;
  candidateId: string;
  candidateName: string;
  positionName: string;
  offerId: string;
}

export interface EmployerSelection360 {
  id: string;
  selectedAt: string;
  candidateId: string;
  candidateName: string;
  positionName: string;
  offerId: string | null;
}

export interface EmployerFollow360 {
  userId: string;
  email: string;
  role: string;
  createdAt: string;
}

export interface Employer360 {
  profileId: string;
  userId: string;
  companyName: string;
  siret: string;
  siretVerified: boolean;
  firstName: string;
  lastName: string;
  positionInCompany: string;
  email: string;
  phone: string;
  employerEmail: string;
  employerPhone: string;
  address: string;
  zipcode: string;
  city: string;
  /** Tranche déclarée (`1_9` … `250_plus`), null si non renseignée. */
  employeeRange: string | null;
  /** Valeur représentative de la tranche, ou entier saisi avant la migration 080. */
  employeeCount: number | null;
  status: string;
  role: string;
  preferredLang: string | null;
  seedTag: string | null;
  registeredAt: string;
  registeredAtRaw: string | null;
  updatedAt: string | null;
  lastLoginAt: string | null;
  profileComplete: boolean;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
  subscriptionActive: boolean;
  plan: 'free' | 'premium';
  stripeCustomerId: string | null;
  hasPushToken: boolean;
  revision: number | null;
  contentHash: string | null;
  establishments: EmployerEstablishment360[];
  offers: EmployerOffer360[];
  videos: EmployerVideo360[];
  subscriptions: EmployerSubscription360[];
  invoices: EmployerInvoice360[];
  applications: EmployerApplication360[];
  selections: EmployerSelection360[];
  following: EmployerFollow360[];
  followers: EmployerFollow360[];
  blocked: { userId: string; email: string; createdAt: string }[];
  conversations: { id: string; preview: string; lastMessageAt: string | null; otherEmail: string }[];
  changeLog: AccountChange360[];
  stats: {
    applicationsReceived: number;
    profileViewsMade: number;
    favoritesMade: number;
    activeOffers: number;
    candidatesContacted: number;
    videosWatched: number;
    conversations: number;
    following: number;
    followers: number;
  };
}

// ─── Fetchers ────────────────────────────────────────────────────────

const fmtDate = (d: string | null) => {
  if (!d) return '';
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmy = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return d;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString('fr-FR');
};
const fmtDateTime = (d: string | null) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : null;

const asStrArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : [];

const parseLangs = (raw: unknown): { language: string; level: number }[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x: any) => {
      if (typeof x === 'string') return { language: x, level: 0 };
      return { language: String(x?.language || x.lang || ''), level: Number(x?.level) || 0 };
    })
    .filter((x) => x.language);
};

const geoToText = (loc: any): string | null => {
  if (!loc) return null;
  if (typeof loc === 'string') return loc;
  if (Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
    const [lng, lat] = loc.coordinates;
    return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
  }
  if (loc.lat != null && loc.lng != null) {
    return `${Number(loc.lat).toFixed(5)}, ${Number(loc.lng).toFixed(5)}`;
  }
  return null;
};

const uniqueIds = (ids: (string | null | undefined)[]) =>
  [...new Set(ids.filter((id): id is string => !!id))];

function mapChangeLog(rows: any[] | null): AccountChange360[] {
  return (rows || []).map((r: any) => ({
    id: r.id,
    occurredAt: fmtDateTime(r.occurred_at) || '',
    tableName: r.table_name,
    rowId: r.row_id || null,
    op: r.op,
    source: r.source || 'unknown',
    origin: r.origin || null,
    actorId: r.actor_id || null,
    changedFields: asStrArray(r.changed_fields),
    clearedFields: asStrArray(r.cleared_fields),
    blockedClears: (r.blocked_clears && typeof r.blocked_clears === 'object') ? r.blocked_clears : {},
    changes: (r.changes && typeof r.changes === 'object') ? r.changes : {},
    hasWipe: !!r.has_wipe,
  }));
}

export async function fetchCandidate360(profileId: string): Promise<Candidate360> {
  const { data: c, error } = await supabase
    .from('candidate_profiles')
    .select('*')
    .eq('id', profileId)
    .single();
  if (error || !c) {
    logger.error('fetch_candidate_360', 'userDetailService', { profileId, error: error?.message });
    throw error || new Error('Candidat introuvable');
  }

  const [profileRes, expRes, langRes, videoRes, appRes, viewsRes, favRes, swipeRes, logRes] = await Promise.all([
    supabase.from('profiles').select('email, phone, status, seed_tag, last_login_at').eq('id', c.user_id).single(),
    supabase.from('candidate_experiences')
      .select('id, company_name, company_city, years_experience, work_rhythm, sector:sectors(name_fr), position:positions(name_fr)')
      .eq('candidate_id', profileId)
      .order('sort_order'),
    supabase.from('candidate_languages').select('language, level').eq('candidate_id', profileId),
    supabase.from('candidate_videos')
      .select('id, thumbnail_url, status, analysis_verdict, media_type, created_at')
      .eq('candidate_id', profileId)
      .order('created_at', { ascending: false }),
    supabase.from('applications')
      .select('id, status, applied_at, offer:job_offers(id, position_name, establishment_name, establishment_city)')
      .eq('candidate_id', profileId)
      .order('applied_at', { ascending: false }),
    supabase.from('profile_views').select('id', { count: 'exact', head: true }).eq('candidate_id', profileId),
    supabase.from('favorites').select('id', { count: 'exact', head: true }).eq('user_id', c.user_id),
    supabase.from('swipe_actions').select('id', { count: 'exact', head: true }).eq('user_id', c.user_id),
    supabase.from('account_change_log')
      .select('*')
      .eq('owner_user_id', c.user_id)
      .order('occurred_at', { ascending: false })
      .limit(200),
  ]);

  const p = profileRes.data;
  logger.technical('fetch_candidate_360_ok', 'userDetailService', { profileId });

  return {
    profileId: c.id,
    userId: c.user_id,
    firstName: c.first_name || '',
    lastName: c.last_name || '',
    email: p?.email || '',
    phone: p?.phone || '',
    city: c.city || '',
    department: c.department || '',
    photoUrl: c.photo_url,
    tagline: c.tagline_fr || c.tagline || '',
    status: p?.status || 'pending_verification',
    seedTag: p?.seed_tag || null,
    registeredAt: fmtDate(c.created_at),
    lastLoginAt: fmtDateTime(p?.last_login_at || null),
    profileComplete: c.profile_complete || false,
    identityVerified: c.identity_verified || false,
    hasDrivingLicense: c.has_driving_license || false,
    mobilityMinutes: c.mobility_minutes,
    desiredWorkRhythm: c.desired_work_rhythm,
    desiredContractTypes: c.desired_contract_types,
    desiredSalaryMin: c.desired_salary_min,
    desiredSalaryMax: c.desired_salary_max,
    availabilityType: c.availability_type,
    availabilityDate: c.availability_date ? fmtDate(c.availability_date) : null,
    languages: (langRes.data || []).map((l: any) => ({ language: l.language, level: l.level })),
    experiences: (expRes.data || []).map((e: any) => ({
      id: e.id,
      companyName: e.company_name || '',
      city: e.company_city || '',
      sectorName: e.sector?.name_fr || '',
      positionName: e.position?.name_fr || '',
      yearsExperience: e.years_experience || '',
      workRhythm: e.work_rhythm,
    })),
    videos: (videoRes.data || []).map((v: any) => ({
      id: v.id,
      thumbnailUrl: v.thumbnail_url,
      status: v.status,
      analysisVerdict: v.analysis_verdict,
      mediaType: v.media_type,
      createdAt: fmtDate(v.created_at),
    })),
    applications: (appRes.data || []).map((a: any) => ({
      id: a.id,
      status: a.status,
      appliedAt: fmtDate(a.applied_at),
      positionName: a.offer?.position_name || '',
      establishmentName: a.offer?.establishment_name || '',
      city: a.offer?.establishment_city || '',
      offerId: a.offer?.id || '',
    })),
    changeLog: mapChangeLog(logRes.data),
    stats: {
      profileViewsReceived: viewsRes.count || 0,
      favoritesMade: favRes.count || 0,
      swipesMade: swipeRes.count || 0,
    },
  };
}

export async function fetchEmployer360(profileId: string): Promise<Employer360> {
  const { data: e, error } = await supabase
    .from('employer_profiles')
    .select('*')
    .eq('id', profileId)
    .single();
  if (error || !e) {
    logger.error('fetch_employer_360', 'userDetailService', { profileId, error: error?.message });
    throw error || new Error('Recruteur introuvable');
  }

  const userId = e.user_id as string;

  const [
    profileRes,
    estRes,
    offersRes,
    videosRes,
    viewsRes,
    favRes,
    videoViewsRes,
    subRes,
    selRes,
    followingRes,
    followersRes,
    blocksRes,
    convRes,
    logRes,
  ] = await Promise.all([
    supabase
      .from('profiles')
      .select('email, phone, status, role, seed_tag, last_login_at, preferred_lang, created_at, updated_at, expo_push_token')
      .eq('id', userId)
      .single(),
    supabase.from('employer_establishments').select('*').eq('employer_id', profileId).order('created_at', { ascending: true }),
    supabase.from('job_offers').select('*').eq('employer_id', profileId).order('created_at', { ascending: false }),
    supabase.from('employer_videos').select('*').eq('employer_id', profileId).order('created_at', { ascending: false }),
    supabase.from('profile_views').select('id', { count: 'exact', head: true }).eq('employer_id', profileId),
    supabase.from('favorites').select('id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.from('video_interactions').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('interaction', 'view'),
    supabase.from('subscriptions').select('*').eq('employer_id', profileId).order('created_at', { ascending: false }),
    supabase
      .from('employer_selections')
      .select('id, selected_at, candidate_id, job_offer_id')
      .eq('employer_id', profileId)
      .order('selected_at', { ascending: false }),
    supabase.from('follows').select('followed_user_id, created_at').eq('follower_id', userId),
    supabase.from('follows').select('follower_id, created_at').eq('followed_user_id', userId),
    supabase.from('user_blocks').select('blocked_id, created_at').eq('blocker_id', userId),
    supabase
      .from('conversations')
      .select('id, user_low, user_high, last_message_at, last_message_preview')
      .or(`user_low.eq.${userId},user_high.eq.${userId}`)
      .order('last_message_at', { ascending: false })
      .limit(50),
    supabase.from('account_change_log')
      .select('*')
      .eq('owner_user_id', userId)
      .order('occurred_at', { ascending: false })
      .limit(200),
  ]);

  const queryErrors = [
    ['profile', profileRes], ['establishments', estRes], ['offers', offersRes],
    ['videos', videosRes], ['subscriptions', subRes], ['selections', selRes],
    ['following', followingRes], ['followers', followersRes], ['blocks', blocksRes],
    ['conversations', convRes], ['changelog', logRes],
  ] as const;
  for (const [name, res] of queryErrors) {
    if (res.error) {
      logger.error(`fetch_employer_360_${name}`, 'userDetailService', { profileId, error: res.error.message });
    }
  }

  const offersRaw = offersRes.data || [];
  const offerIds = offersRaw.map((o: any) => o.id as string);
  const subIds = (subRes.data || []).map((s: any) => s.id as string);
  const relatedUserIds = uniqueIds([
    ...(followingRes.data || []).map((f: any) => f.followed_user_id),
    ...(followersRes.data || []).map((f: any) => f.follower_id),
    ...(blocksRes.data || []).map((b: any) => b.blocked_id),
    ...(convRes.data || []).flatMap((c: any) => [c.user_low, c.user_high]),
  ]);

  const [appRes, invRes, peopleRes] = await Promise.all([
    offerIds.length
      ? supabase
          .from('applications')
          .select('id, status, applied_at, viewed_at, selected_at, candidate_id, job_offer_id')
          .in('job_offer_id', offerIds)
          .order('applied_at', { ascending: false })
      : Promise.resolve({ data: [] as any[] }),
    subIds.length
      ? supabase.from('invoices').select('*').in('subscription_id', subIds).order('created_at', { ascending: false })
      : Promise.resolve({ data: [] as any[] }),
    relatedUserIds.length
      ? supabase.from('profiles').select('id, email, role').in('id', relatedUserIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);

  const candIds = uniqueIds([
    ...(appRes.data || []).map((a: any) => a.candidate_id),
    ...(selRes.data || []).map((s: any) => s.candidate_id),
  ]);
  const { data: candRows } = candIds.length
    ? await supabase.from('candidate_profiles').select('id, first_name, last_name').in('id', candIds)
    : { data: [] as any[] };

  const peopleById = new Map((peopleRes.data || []).map((p: any) => [p.id, p]));
  const candById = new Map((candRows || []).map((c: any) => [c.id, c]));
  const offerById = new Map(offersRaw.map((o: any) => [o.id, o]));
  const candName = (id: string) => {
    const c = candById.get(id);
    return c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—' : '—';
  };
  const personLabel = (id: string) => peopleById.get(id)?.email || id;

  const p = profileRes.data;
  const offers: EmployerOffer360[] = offersRaw.map((o: any) => ({
    id: o.id,
    sectorCode: o.sector_code || '',
    positionCode: o.position_code || '',
    positionName: o.position_name || '',
    establishmentName: o.establishment_name || '',
    establishmentAddress: o.establishment_address || '',
    establishmentZipcode: o.establishment_zipcode || '',
    city: o.establishment_city || '',
    yearsExperience: o.years_experience || null,
    contractType: o.contract_type || null,
    contractStartDate: o.contract_start_date ? fmtDate(o.contract_start_date) : null,
    contractEndDate: o.contract_end_date ? fmtDate(o.contract_end_date) : null,
    workDuration: o.work_duration || null,
    workRhythm: asStrArray(o.work_rhythm),
    salaryMin: o.salary_min ?? null,
    salaryMax: o.salary_max ?? null,
    salaryType: o.salary_type || null,
    requiredDrivingLicense: !!o.required_driving_license,
    requiredDiplomas: asStrArray(o.required_diplomas),
    advantages: asStrArray(o.advantages),
    otherInfo: o.other_info || '',
    requiredSkills: asStrArray(o.required_skills),
    requiredLanguages: parseLangs(o.required_languages),
    status: o.status,
    applicationsCount: o.applications_count || 0,
    publishedAt: o.published_at ? fmtDate(o.published_at) : null,
    createdAt: fmtDate(o.created_at),
    updatedAt: o.updated_at ? fmtDateTime(o.updated_at) : null,
    seedTag: o.seed_tag || null,
    revision: o.revision ?? null,
  }));

  logger.technical('fetch_employer_360_ok', 'userDetailService', { profileId, offers: offers.length });

  return {
    profileId: e.id,
    userId,
    companyName: e.company_name || '',
    siret: e.siret || '',
    siretVerified: e.siret_verified || false,
    firstName: e.first_name || '',
    lastName: e.last_name || '',
    positionInCompany: e.position_in_company || '',
    email: p?.email || e.email || '',
    phone: p?.phone || e.phone || '',
    employerEmail: e.email || '',
    employerPhone: e.phone || '',
    address: e.company_address || '',
    zipcode: e.company_zipcode || '',
    city: e.company_city || '',
    employeeRange: e.employee_range ?? null,
    employeeCount: e.employee_count ?? null,
    status: p?.status || 'pending_verification',
    role: p?.role || 'employer',
    preferredLang: p?.preferred_lang || null,
    seedTag: p?.seed_tag || null,
    registeredAt: fmtDate(e.created_at),
    registeredAtRaw: e.created_at || null,
    updatedAt: fmtDateTime(e.updated_at || p?.updated_at || null),
    lastLoginAt: fmtDateTime(p?.last_login_at || null),
    profileComplete: e.profile_complete || false,
    emailVerified: e.email_verified || false,
    emailVerifiedAt: fmtDateTime(e.email_verified_at || null),
    subscriptionActive: e.subscription_active || false,
    plan: (subRes.data || []).some((s: any) => s.plan === 'premium' && s.status === 'active') ? 'premium' : 'free',
    stripeCustomerId: e.stripe_customer_id || null,
    hasPushToken: !!p?.expo_push_token,
    revision: e.revision ?? null,
    contentHash: e.content_hash || null,
    establishments: (estRes.data || []).map((est: any) => ({
      id: est.id,
      name: est.name || '',
      zipcode: est.zipcode || '',
      city: est.city || '',
      locationText: geoToText(est.location),
      logoUrl: est.logo_url || null,
      photos: asStrArray(est.photos),
      createdAt: fmtDate(est.created_at),
      updatedAt: est.updated_at ? fmtDateTime(est.updated_at) : null,
    })),
    offers,
    videos: (videosRes.data || []).map((v: any) => ({
      id: v.id,
      thumbnailUrl: v.thumbnail_url,
      videoUrl: v.video_url || null,
      status: v.status,
      title: v.title,
      videoType: v.video_type,
      mediaType: v.media_type || null,
      durationSeconds: v.duration_seconds ?? null,
      textOverlay: v.text_overlay || null,
      tags: asStrArray(v.tags),
      analysisStatus: v.analysis_status || null,
      analysisVerdict: v.analysis_verdict || null,
      offerId: v.offer_id || null,
      muxPlaybackId: v.mux_playback_id || null,
      createdAt: fmtDate(v.created_at),
    })),
    subscriptions: (subRes.data || []).map((s: any) => ({
      id: s.id,
      plan: s.plan || 'free',
      status: s.status,
      paymentMethod: s.payment_method || null,
      amountHt: s.amount_ht != null ? Number(s.amount_ht) : null,
      stripeCustomerId: s.stripe_customer_id || null,
      stripeSubscriptionId: s.stripe_subscription_id || null,
      currentPeriodStart: fmtDateTime(s.current_period_start || null),
      currentPeriodEnd: fmtDateTime(s.current_period_end || null),
      cancelAtPeriodEnd: !!s.cancel_at_period_end,
      createdAt: fmtDate(s.created_at),
    })),
    invoices: (invRes.data || []).map((i: any) => ({
      id: i.id,
      stripeInvoiceId: i.stripe_invoice_id || null,
      amount: i.amount != null ? Number(i.amount) : null,
      status: i.status,
      periodStart: i.period_start ? fmtDate(i.period_start) : null,
      periodEnd: i.period_end ? fmtDate(i.period_end) : null,
      paidAt: fmtDateTime(i.paid_at || null),
      pdfUrl: i.pdf_url || null,
      createdAt: fmtDate(i.created_at),
    })),
    applications: (appRes.data || []).map((a: any) => ({
      id: a.id,
      status: a.status,
      appliedAt: fmtDate(a.applied_at),
      viewedAt: a.viewed_at ? fmtDateTime(a.viewed_at) : null,
      selectedAt: a.selected_at ? fmtDateTime(a.selected_at) : null,
      candidateId: a.candidate_id,
      candidateName: candName(a.candidate_id),
      positionName: offerById.get(a.job_offer_id)?.position_name || '',
      offerId: a.job_offer_id,
    })),
    selections: (selRes.data || []).map((s: any) => ({
      id: s.id,
      selectedAt: fmtDateTime(s.selected_at) || '',
      candidateId: s.candidate_id,
      candidateName: candName(s.candidate_id),
      positionName: offerById.get(s.job_offer_id)?.position_name || '',
      offerId: s.job_offer_id || null,
    })),
    following: (followingRes.data || []).map((f: any) => ({
      userId: f.followed_user_id,
      email: personLabel(f.followed_user_id),
      role: peopleById.get(f.followed_user_id)?.role || '',
      createdAt: fmtDate(f.created_at),
    })),
    followers: (followersRes.data || []).map((f: any) => ({
      userId: f.follower_id,
      email: personLabel(f.follower_id),
      role: peopleById.get(f.follower_id)?.role || '',
      createdAt: fmtDate(f.created_at),
    })),
    blocked: (blocksRes.data || []).map((b: any) => ({
      userId: b.blocked_id,
      email: personLabel(b.blocked_id),
      createdAt: fmtDate(b.created_at),
    })),
    conversations: (convRes.data || []).map((c: any) => {
      const otherId = c.user_low === userId ? c.user_high : c.user_low;
      return {
        id: c.id,
        preview: c.last_message_preview || '',
        lastMessageAt: fmtDateTime(c.last_message_at || null),
        otherEmail: personLabel(otherId),
      };
    }),
    changeLog: mapChangeLog(logRes.data),
    stats: {
      applicationsReceived: offers.reduce((sum, o) => sum + o.applicationsCount, 0),
      profileViewsMade: viewsRes.count || 0,
      favoritesMade: favRes.count || 0,
      activeOffers: offers.filter((o) => o.status === 'active').length,
      candidatesContacted: (selRes.data || []).length,
      videosWatched: videoViewsRes.count || 0,
      conversations: (convRes.data || []).length,
      following: (followingRes.data || []).length,
      followers: (followersRes.data || []).length,
    },
  };
}

// ─── Comptes admin ───────────────────────────────────────────────────

export interface AdminAccount {
  userId: string;
  email: string;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

export async function fetchAdminAccounts(): Promise<AdminAccount[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, status, created_at, last_login_at')
    .eq('role', 'admin')
    .neq('id', POWERJOBS_USER_ID) // compte support technique, pas un admin humain
    .order('created_at', { ascending: true });
  if (error) {
    logger.error('fetch_admin_accounts', 'userDetailService', { error: error.message });
    throw error;
  }
  return (data || []).map((p: any) => ({
    userId: p.id,
    email: p.email || '',
    status: p.status || 'active',
    createdAt: fmtDate(p.created_at),
    lastLoginAt: fmtDateTime(p.last_login_at),
  }));
}

export async function createAdminAccount(email: string, password: string): Promise<void> {
  logger.functional('create_admin_account', 'userDetailService', { email });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-admin`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ email, password }),
    }
  );
  const body = await res.json();
  if (!res.ok) {
    logger.error('create_admin_account', 'userDetailService', { email, error: body.error });
    throw new Error(body.error || 'Création échouée');
  }
  logger.technical('create_admin_account_ok', 'userDetailService', { email });
}
